import { writable, derived } from 'svelte/store';

// Route state
function createRouteStore() {
  const { subscribe, set, update } = writable({
    postcodes: [],
    coordinates: [],
    startLocation: null,
    endLocation: null,
    optimizedSequence: [],
    currentStopIndex: 0,
    completedStops: [],
    distanceMatrix: [],
    timeMatrix: [],
    isOptimized: false
  });

  return {
    subscribe,

    // Add postcode to the list
    addPostcode: (postcode) => update(state => ({
      ...state,
      postcodes: [...state.postcodes, postcode.toUpperCase()],
      isOptimized: false
    })),

    // Remove postcode at index
    removePostcode: (index) => update(state => ({
      ...state,
      postcodes: state.postcodes.filter((_, i) => i !== index),
      isOptimized: false
    })),

    // Set all postcodes at once (bulk paste)
    setPostcodes: (postcodes) => update(state => ({
      ...state,
      postcodes: postcodes.map(p => p.toUpperCase()),
      isOptimized: false
    })),

    // Set starting location
    setStartLocation: (location) => update(state => ({
      ...state,
      startLocation: location
    })),

    // Set end location (optional)
    setEndLocation: (location) => update(state => ({
      ...state,
      endLocation: location
    })),

    // Set optimized route data
    setOptimizedRoute: (data) => update(state => ({
      ...state,
      coordinates: data.coordinates,
      optimizedSequence: data.optimizedSequence,
      distanceMatrix: data.distanceMatrix,
      timeMatrix: data.timeMatrix,
      isOptimized: true,
      currentStopIndex: 0
    })),

    // Mark current stop as complete
    markComplete: () => update(state => {
      const currentStop = {
        postcode: state.postcodes[state.optimizedSequence[state.currentStopIndex]],
        sequence: state.currentStopIndex + 1,
        completedAt: new Date().toISOString(),
        distanceKm: state.currentStopIndex > 0
          ? state.distanceMatrix[state.optimizedSequence[state.currentStopIndex - 1]][state.optimizedSequence[state.currentStopIndex]]
          : 0,
        timeMin: state.currentStopIndex > 0
          ? state.timeMatrix[state.optimizedSequence[state.currentStopIndex - 1]][state.optimizedSequence[state.currentStopIndex]]
          : 0
      };

      return {
        ...state,
        completedStops: [...state.completedStops, currentStop],
        currentStopIndex: state.currentStopIndex + 1
      };
    }),

    // Move postcode to priority (first in route)
    prioritizePostcode: (index) => update(state => {
      const newSequence = [...state.optimizedSequence];
      const postcodeIndex = newSequence.indexOf(index);
      if (postcodeIndex > state.currentStopIndex) {
        newSequence.splice(postcodeIndex, 1);
        newSequence.splice(state.currentStopIndex, 0, index);
      }
      return {
        ...state,
        optimizedSequence: newSequence
      };
    }),

    // Reset all route data
    reset: () => set({
      postcodes: [],
      coordinates: [],
      startLocation: null,
      endLocation: null,
      optimizedSequence: [],
      currentStopIndex: 0,
      completedStops: [],
      distanceMatrix: [],
      timeMatrix: [],
      isOptimized: false
    })
  };
}

export const routeStore = createRouteStore();

// Derived stores for computed values
export const totalStops = derived(
  routeStore,
  $route => $route.optimizedSequence.length
);

export const completedCount = derived(
  routeStore,
  $route => $route.completedStops.length
);

export const remainingStops = derived(
  routeStore,
  $route => $route.optimizedSequence.length - $route.currentStopIndex
);

export const currentStop = derived(
  routeStore,
  $route => {
    if ($route.currentStopIndex >= $route.optimizedSequence.length) return null;
    const postcodeIndex = $route.optimizedSequence[$route.currentStopIndex];
    return {
      postcode: $route.postcodes[postcodeIndex],
      sequence: $route.currentStopIndex + 1,
      coordinate: $route.coordinates[postcodeIndex]
    };
  }
);

export const totalDistance = derived(
  routeStore,
  $route => {
    if (!$route.distanceMatrix.length || !$route.optimizedSequence.length) return 0;
    let total = 0;
    for (let i = 0; i < $route.optimizedSequence.length - 1; i++) {
      total += $route.distanceMatrix[$route.optimizedSequence[i]][$route.optimizedSequence[i + 1]];
    }
    return total;
  }
);

export const totalTime = derived(
  routeStore,
  $route => {
    if (!$route.timeMatrix.length || !$route.optimizedSequence.length) return 0;
    let total = 0;
    for (let i = 0; i < $route.optimizedSequence.length - 1; i++) {
      total += $route.timeMatrix[$route.optimizedSequence[i]][$route.optimizedSequence[i + 1]];
    }
    return total;
  }
);
